import { Card } from "../../components/ui/Card";
import type { PreferredUnits } from "../athlete-profile/types/athleteProfile";
import type { AthleteIntelligenceSnapshot, IntelligenceMetricName, TrendDirection } from "../train/athleteIntelligence";
import type { DailyHealthRecord } from "../health/dailyHealthTypes";
import { formatMinutesAsDuration, formatWeightFromKilograms } from "../../lib/unitPreferences";

export const MIN_RIDES_FOR_CURRENT_TREND = 14;
export const MIN_RIDES_FOR_LONG_TERM_TREND = 16;

type IntelligencePageProps = {
  athleteIntelligence: AthleteIntelligenceSnapshot;
  dailyHealthRecords: DailyHealthRecord[];
  historySummary?: {
    rideCount: number;
    rideDateRange: { start: string | null; end: string | null };
    dailyHealthCount: number;
    dailyHealthDateRange: { start: string | null; end: string | null };
  };
  preferredUnits?: PreferredUnits;
};

type TrendDisplay = {
  label: string;
  status: string;
  explanation: string;
};

function formatDate(value: string | null): string {
  if (!value) return "Not available";
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;
  return new Date(parsed).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}

function formatMetricValue(value: number | null, decimals = 2): string {
  if (value === null || !Number.isFinite(value)) return "Not available";
  return value.toFixed(decimals);
}

function dataCoverageLabel(snapshot: AthleteIntelligenceSnapshot): string {
  if (snapshot.rideCount === 0) return "No completed analyses yet";
  if (snapshot.highestConfidenceMetrics.length === 0) return "Low coverage";
  const strong = snapshot.highestConfidenceMetrics.filter(metric => metric.sampleCount >= Math.max(1, Math.floor(snapshot.rideCount * 0.7))).length;
  if (strong >= 2) return "High coverage";
  if (strong >= 1) return "Medium coverage";
  return "Low coverage";
}

function trendLabel(value: TrendDirection): string {
  if (value === "improving") return "Improving";
  if (value === "declining") return "Declining";
  return "Stable";
}

function buildTrendDisplay(input: {
  rideCount: number;
  trend: TrendDirection;
  minSamples: number;
  windowLabel: string;
}): TrendDisplay {
  if (input.rideCount < input.minSamples) {
    return {
      label: input.windowLabel,
      status: "Insufficient data",
      explanation: `Requires at least ${input.minSamples} completed rides.`
    };
  }

  return {
    label: input.windowLabel,
    status: trendLabel(input.trend),
    explanation: `Computed from ${input.windowLabel.toLowerCase()}.`
  };
}

function metricLabel(metric: IntelligenceMetricName): string {
  switch (metric) {
    case "averageNp":
      return "Normalized power";
    case "averageIf":
      return "Intensity factor";
    case "averagePower":
      return "Average power";
    case "averageVariabilityIndex":
      return "Variability index";
    case "averageDecoupling":
      return "Aerobic decoupling";
    case "averageDurability":
      return "Durability";
    case "averageRespiratoryDrift":
      return "Respiratory drift";
    case "averageRideConfidence":
      return "Analysis confidence";
    default:
      return metric;
  }
}

function metricUnit(metric: IntelligenceMetricName): string {
  switch (metric) {
    case "averageNp":
    case "averagePower":
      return "W";
    case "averageIf":
    case "averageVariabilityIndex":
      return "";
    case "averageDecoupling":
    case "averageDurability":
    case "averageRespiratoryDrift":
    case "averageRideConfidence":
      return "%";
    default:
      return "";
  }
}

function buildHistoricalMetricRows(snapshot: AthleteIntelligenceSnapshot): Array<{ metric: IntelligenceMetricName; value: number }> {
  const ordered: IntelligenceMetricName[] = [
    "averageNp",
    "averageIf",
    "averagePower",
    "averageVariabilityIndex",
    "averageDecoupling",
    "averageDurability",
    "averageRespiratoryDrift",
    "averageRideConfidence"
  ];

  return ordered
    .map(metric => ({ metric, value: snapshot.historicalMetrics[metric] }))
    .filter((row): row is { metric: IntelligenceMetricName; value: number } => row.value !== null && Number.isFinite(row.value));
}

function formatWindowMetric(metric: IntelligenceMetricName, value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "Not available";
  }

  const unit = metricUnit(metric);
  if (unit === "%") {
    return `${value.toFixed(2)}%`;
  }
  if (unit === "W") {
    return `${value.toFixed(0)} W`;
  }
  return value.toFixed(3);
}

function latestRecord(records: DailyHealthRecord[]): DailyHealthRecord | null {
  if (records.length === 0) return null;
  return [...records].sort((left, right) => right.localDate.localeCompare(left.localDate))[0];
}

function sampleCount(records: DailyHealthRecord[], selector: (record: DailyHealthRecord) => number | null): number {
  return records.filter(record => {
    const value = selector(record);
    return value !== null && Number.isFinite(value);
  }).length;
}

function renderDailyHealthValue(value: number | null, suffix: string): string {
  if (value === null || !Number.isFinite(value)) return "Not available";
  return suffix ? `${value.toFixed(2)} ${suffix}` : value.toFixed(2);
}

export function IntelligencePage({ athleteIntelligence, dailyHealthRecords, historySummary, preferredUnits = "imperial" }: IntelligencePageProps) {
  const resolvedHistorySummary = historySummary ?? {
    rideCount: athleteIntelligence.rideCount,
    rideDateRange: { start: null, end: athleteIntelligence.lastAnalysisDate },
    dailyHealthCount: dailyHealthRecords.length,
    dailyHealthDateRange: {
      start: dailyHealthRecords.length > 0 ? dailyHealthRecords[dailyHealthRecords.length - 1].localDate : null,
      end: dailyHealthRecords.length > 0 ? dailyHealthRecords[0].localDate : null
    }
  };

  const currentTrendDisplay = buildTrendDisplay({
    rideCount: athleteIntelligence.rideCount,
    trend: athleteIntelligence.currentTrend,
    minSamples: MIN_RIDES_FOR_CURRENT_TREND,
    windowLabel: "Last 7 rides vs prior 7 rides"
  });
  const longTermTrendDisplay = buildTrendDisplay({
    rideCount: athleteIntelligence.rideCount,
    trend: athleteIntelligence.longTermTrend,
    minSamples: MIN_RIDES_FOR_LONG_TERM_TREND,
    windowLabel: "Last 30 days vs prior 60 days"
  });

  const historicalMetrics = buildHistoricalMetricRows(athleteIntelligence);
  const latestDailyHealth = latestRecord(dailyHealthRecords);

  const dailyHealthSamples = {
    bodyWeight: sampleCount(dailyHealthRecords, record => record.bodyComposition.bodyWeightKg),
    hrvSdnn: sampleCount(dailyHealthRecords, record => record.recovery.hrvSdnnMs),
    restingHeartRate: sampleCount(dailyHealthRecords, record => record.recovery.restingHeartRateBpm),
    sleepDuration: sampleCount(dailyHealthRecords, record => record.sleep.totalSleepMinutes),
    respiratoryRate: sampleCount(dailyHealthRecords, record => record.recovery.respiratoryRateBpm)
  };

  return (
    <div className="command-center-layout">
      <Card>
        <h2>Athlete Intelligence overview</h2>
        <p className="command-note">Data used: persisted completed ride analyses and Apple Health local import records.</p>
        <div className="command-grid metrics-3">
          <article className="summary-item">
            <span>Completed ride count</span>
            <strong>{resolvedHistorySummary.rideCount}</strong>
            <small>Source: persisted completed ride analyses</small>
          </article>
          <article className="summary-item">
            <span>Ride date range</span>
            <strong>
              {resolvedHistorySummary.rideDateRange.start
                ? `${formatDate(resolvedHistorySummary.rideDateRange.start)} to ${formatDate(resolvedHistorySummary.rideDateRange.end)}`
                : "Not available"}
            </strong>
            <small>Source: AthleteHistory index</small>
          </article>
          <article className="summary-item">
            <span>Data coverage</span>
            <strong>{dataCoverageLabel(athleteIntelligence)}</strong>
            <small>Source: persisted completed ride analyses</small>
          </article>
          <article className="summary-item">
            <span>Daily Health record count</span>
            <strong>{resolvedHistorySummary.dailyHealthCount}</strong>
            <small>Source: Apple Health local import</small>
          </article>
          <article className="summary-item">
            <span>Daily Health date range</span>
            <strong>
              {resolvedHistorySummary.dailyHealthDateRange.start
                ? `${resolvedHistorySummary.dailyHealthDateRange.start} to ${resolvedHistorySummary.dailyHealthDateRange.end}`
                : "Not available"}
            </strong>
            <small>Source: AthleteHistory index</small>
          </article>
          <article className="summary-item">
            <span>Latest completed analysis</span>
            <strong>{formatDate(athleteIntelligence.lastAnalysisDate)}</strong>
            <small>Source: persisted completed ride analyses</small>
          </article>
        </div>
        <div className="command-grid metrics-2" style={{ marginTop: 12 }}>
          <article className="summary-item">
            <span>{currentTrendDisplay.label}</span>
            <strong>{currentTrendDisplay.status}</strong>
            <small>{currentTrendDisplay.explanation}</small>
          </article>
          <article className="summary-item">
            <span>{longTermTrendDisplay.label}</span>
            <strong>{longTermTrendDisplay.status}</strong>
            <small>{longTermTrendDisplay.explanation}</small>
          </article>
        </div>
      </Card>

      <Card>
        <h2>Historical ride metrics</h2>
        <p className="command-note">Source: persisted completed ride analyses (includes Tymewear-derived respiratory drift when available).</p>
        {historicalMetrics.length === 0 ? (
          <p className="command-note">No historical metrics available yet.</p>
        ) : (
          <div className="command-grid metrics-3">
            {historicalMetrics.map(item => (
              <article key={item.metric} className="summary-item">
                <span>{metricLabel(item.metric)}</span>
                <strong>{formatWindowMetric(item.metric, item.value)}</strong>
                <small>Provenance: persisted completed ride analyses</small>
              </article>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <h2>Rolling windows</h2>
        <div className="command-grid metrics-3">
          <article className="summary-item">
            <span>Last 7 rides</span>
            <strong>{formatWindowMetric("averageNp", athleteIntelligence.rollingAverages.last7Rides.averageNp ?? null)}</strong>
            <small>Source: persisted completed ride analyses</small>
          </article>
          <article className="summary-item">
            <span>Last 30 days</span>
            <strong>{formatWindowMetric("averageNp", athleteIntelligence.rollingAverages.last30Days.averageNp ?? null)}</strong>
            <small>Source: persisted completed ride analyses</small>
          </article>
          <article className="summary-item">
            <span>Last 90 days</span>
            <strong>{formatWindowMetric("averageNp", athleteIntelligence.rollingAverages.last90Days.averageNp ?? null)}</strong>
            <small>Source: persisted completed ride analyses</small>
          </article>
        </div>
      </Card>

      <Card>
        <h2>Daily Health context</h2>
        <p className="command-note">Source: Apple Health local import.</p>
        {latestDailyHealth ? (
          <>
            <p className="command-note">Latest import date: {latestDailyHealth.localDate}</p>
            <div className="command-grid metrics-3">
              <article className="summary-item">
                <span>Body weight</span>
                <strong>{latestDailyHealth.bodyComposition.bodyWeightKg === null ? "Not available" : formatWeightFromKilograms(latestDailyHealth.bodyComposition.bodyWeightKg, preferredUnits)}</strong>
                <small>Samples: {dailyHealthSamples.bodyWeight}</small>
              </article>
              <article className="summary-item">
                <span>HRV SDNN</span>
                <strong>{renderDailyHealthValue(latestDailyHealth.recovery.hrvSdnnMs, "ms")}</strong>
                <small>Samples: {dailyHealthSamples.hrvSdnn}</small>
              </article>
              <article className="summary-item">
                <span>Resting HR</span>
                <strong>{renderDailyHealthValue(latestDailyHealth.recovery.restingHeartRateBpm, "bpm")}</strong>
                <small>Samples: {dailyHealthSamples.restingHeartRate}</small>
              </article>
              <article className="summary-item">
                <span>Sleep duration</span>
                <strong>{latestDailyHealth.sleep.totalSleepMinutes === null ? "Not available" : formatMinutesAsDuration(latestDailyHealth.sleep.totalSleepMinutes)}</strong>
                <small>Samples: {dailyHealthSamples.sleepDuration}</small>
              </article>
              <article className="summary-item">
                <span>Respiratory rate</span>
                <strong>{renderDailyHealthValue(latestDailyHealth.recovery.respiratoryRateBpm, "br/min")}</strong>
                <small>Samples: {dailyHealthSamples.respiratoryRate}</small>
              </article>
            </div>
          </>
        ) : (
          <p className="command-note">No imported daily health records yet. Import Apple Health XML to populate this context.</p>
        )}
      </Card>

      <Card>
        <h2>Data provenance</h2>
        <ul className="command-list compact-list">
          <li>Persisted completed ride analyses</li>
          <li>Apple Health local import</li>
          <li>Athlete profile defaults when source metrics are unavailable</li>
          <li>Insufficient data when minimum sample thresholds are not met</li>
        </ul>
      </Card>
    </div>
  );
}
